/**
 * Dialog for assigning a questionnaire template to a supplier.
 * Uses QualificationTemplate API to list active templates and creates
 * a SupplierQualification on submit.
 */

import { useState, useEffect } from 'react';
import { intelligenceAPI, type QualificationTemplate } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import { Loader2, FileText } from 'lucide-react';
import { toast } from 'sonner';

interface AssignQuestionnaireDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  supplierId: number;
  supplierName: string;
  onAssigned: () => void;
}

/**
 * Dialog that lets a customer assign a questionnaire template to a supplier.
 */
export function AssignQuestionnaireDialog({
  open,
  onOpenChange,
  supplierId,
  supplierName,
  onAssigned,
}: AssignQuestionnaireDialogProps) {
  const [templates, setTemplates] = useState<QualificationTemplate[]>([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) {
      setSelectedTemplateId('');
      return;
    }
    const loadTemplates = async () => {
      setLoadingTemplates(true);
      try {
        const res = await intelligenceAPI.getTemplates({ status: 'active', page_size: 100 });
        const data = res.data.results || [];
        setTemplates(Array.isArray(data) ? data : []);
      } catch (err: unknown) {
        console.error('Failed to load questionnaire templates:', err);
        toast.error('Failed to load questionnaire templates');
      } finally {
        setLoadingTemplates(false);
      }
    };
    loadTemplates();
  }, [open]);

  const handleSubmit = async () => {
    if (!selectedTemplateId) return;

    setSubmitting(true);
    try {
      await intelligenceAPI.createQualification({
        template: Number(selectedTemplateId),
        supplier: supplierId,
        status: 'invited',
      });
      toast.success('Questionnaire assigned successfully');
      onOpenChange(false);
      onAssigned();
    } catch (err: unknown) {
      console.error('Failed to assign questionnaire:', err);
      toast.error('Failed to assign questionnaire');
    } finally {
      setSubmitting(false);
    }
  };

  const TYPE_LABELS: Record<string, string> = {
    compliance: 'Compliance',
    quality: 'Quality',
    capability: 'Capability',
    financial: 'Financial',
    sustainability: 'Sustainability',
    security: 'Security',
    custom: 'Custom',
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Assign Questionnaire
          </DialogTitle>
          <DialogDescription>
            Select a questionnaire template to assign to {supplierName}.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label htmlFor="template-select">Questionnaire Template</Label>
            {loadingTemplates ? (
              <div className="flex items-center gap-2 py-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading templates...
              </div>
            ) : templates.length === 0 ? (
              <p className="text-sm text-muted-foreground py-2">
                No active questionnaire templates found. Create a template first.
              </p>
            ) : (
              <Select value={selectedTemplateId} onValueChange={setSelectedTemplateId}>
                <SelectTrigger id="template-select">
                  <SelectValue placeholder="Select a template..." />
                </SelectTrigger>
                <SelectContent>
                  {templates.map((t) => (
                    <SelectItem key={t.id} value={String(t.id)}>
                      {t.name} ({TYPE_LABELS[t.template_type] || t.template_type})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!selectedTemplateId || submitting}
          >
            {submitting && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
            Assign
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
